"use client"

import { PopulatedTag } from "@/services/tagsService"
import { getTheme } from "@/theme"
import { ChevronRight, Close, ExpandMore } from "@mui/icons-material"
import TreeView from "@mui/lab/TreeView"
import {
    Box,
    IconButton,
    SwipeableDrawer,
    SxProps,
    Theme,
    Typography,
    useMediaQuery,
} from "@mui/material"
import { useParams } from "next/navigation"
import { PropsWithChildren, useState } from "react"
import { useExpanded } from "../context/ExpandedTagsContext"
import { TagsTreeElement } from "./TagsTreeElement"

interface Props {
    rootTag: PopulatedTag
}

const TagsDrawer = ({ children }: PropsWithChildren) => {
    const [isOpen, setIsOpen] = useState(false)
    const handleOpen = () => {
        setIsOpen(true)
    }
    const handleClose = () => {
        setIsOpen(false)
    }

    return (
        <>
            {!isOpen && (
                <Box sx={styles.tagsIcon} onClick={handleOpen}>
                    <IconButton aria-label="Show tags button" sx={styles.tagsButton} size="large">
                        <ChevronRight fontSize="large" />
                    </IconButton>
                </Box>
            )}
            {isOpen && (
                <SwipeableDrawer
                    PaperProps={{ sx: styles.drawer }}
                    open={isOpen}
                    onClose={handleClose}
                    onOpen={handleOpen}
                >
                    {children}
                    <Box
                        sx={{ position: "absolute", top: 14, right: 5, display: { lg: "none" } }}
                        onClick={handleClose}
                    >
                        <Close />
                    </Box>
                </SwipeableDrawer>
            )}
        </>
    )
}

const theme = getTheme()

export const TagsTree = ({ rootTag }: Props) => {
    const [expanded, setExpanded] = useExpanded()
    const params = useParams<{ tagSlug: string }>()
    const selectedTagSlug = params.tagSlug || "thing"

    const isMobile = useMediaQuery(theme.breakpoints.down("xs"))

    return !isMobile ? (
        <Box display={{ xs: "none", lg: "block" }}>
            <Box sx={{ ...styles.tree, ml: 4 }}>
                <TreeView
                    expanded={expanded}
                    defaultCollapseIcon={<ExpandMore />}
                    defaultExpandIcon={<ChevronRight />}
                    onNodeToggle={(_, nodeIds) => {
                        setExpanded(nodeIds)
                    }}
                >
                    <TagsTreeElement
                        selectedTagSlug={selectedTagSlug}
                        tag={{ ...rootTag, name: "All summaries" }}
                    />
                </TreeView>
            </Box>
        </Box>
    ) : (
        <Box display={{ xs: "block", lg: "none" }}>
            <TagsDrawer>
                <Typography variant="h5" component="h3">
                    Categories
                </Typography>
                <Box sx={{ ...styles.tree, p: 0, pt: 1 }}>
                    <TreeView
                        expanded={expanded}
                        defaultCollapseIcon={<ExpandMore />}
                        defaultExpandIcon={<ChevronRight />}
                        onNodeToggle={(_, nodeIds) => {
                            setExpanded(nodeIds)
                        }}
                    >
                        <TagsTreeElement
                            selectedTagSlug={selectedTagSlug}
                            tag={{ ...rootTag, name: "All summaries" }}
                        />
                    </TreeView>
                </Box>
            </TagsDrawer>
        </Box>
    )
}

const styles: Record<string, SxProps<Theme>> = {
    tree: {
        display: "flex",
        flexDirection: "column",
        boxShadow: { lg: "0px 1px 1px 0px rgba(255, 255, 255, 0.25) inset" },
        background: {
            lg: "linear-gradient(180deg, rgba(255, 255, 255, 0.06 ) 0%, rgba(255, 255, 255, 0.1) 100%)",
        },
        maxWidth: "100%",
        boxSizing: "border-box",
        p: 1,
        pr: 2,
        borderRadius: 1,
    },
    tagsIcon: {
        position: "fixed",
        left: 0,
        top: "45%",
        background: "rgba(255, 255, 255, 0.9)",
        border: "1px solid rgba(255, 255, 255, 0.16)",
        borderTopRightRadius: 6,
        borderBottomRightRadius: 6,
        borderLeft: "none",
        zIndex: 1300,
    },
    tagsButton: {
        px: 0,
        color: "background.default",
    },
    drawer: {
        background: theme.palette.background.default,
        p: 1,
        maxWidth: 300,
    },
}
